import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

/**
 * I-1262 (AC-18.10, `docs/concept-mobile-app.md`; *Motion*, `docs/concept-iphone-ux.md`): how a
 * phone sheet or drawer moves, in one place.
 *
 * ⭐ **The dim is the travel.** The backdrop's opacity and the panel's movement share one transition
 * — the same duration and curve in each direction — so the backdrop is exactly as dark as the panel
 * is far along. Jay, 2026-10-07: "the transition in transparency when moving up is very abrupt".
 * Measured before: `Sheet` faded its dim over 200 ms `ease-out` under a 350 ms rise on the movement
 * curve, so the dim ran ahead of the sheet; the two drawers had no motion at all.
 */

/** The rise: UIKit's default spring, as CSS draws it (*Motion*). */
export const SHEET_MOVE_MS = 350;
export const SHEET_MOVE_CURVE = 'cubic-bezier(0.32, 0.72, 0, 1)';
/** The exit: shorter, accelerating away. */
export const SHEET_EXIT_MS = 200;
export const SHEET_EXIT_CURVE = 'cubic-bezier(0.4, 0, 1, 1)';
/** Reduce Motion: nothing moves; panel and dim dissolve together. */
export const SHEET_FADE_MS = 200;
export const SHEET_FADE_CURVE = 'ease-out';

export type SheetMotionPhaseType = 'entering' | 'open' | 'exiting';

/** `<duration> <curve>` for one direction of the motion. */
export function sheetMotionTiming(phase: SheetMotionPhaseType, reducedMotion: boolean): string {
  if (reducedMotion) return `${SHEET_FADE_MS}ms ${SHEET_FADE_CURVE}`;
  return phase === 'exiting'
    ? `${SHEET_EXIT_MS}ms ${SHEET_EXIT_CURVE}`
    : `${SHEET_MOVE_MS}ms ${SHEET_MOVE_CURVE}`;
}

/** The movement's transition (the panel's `transform`). */
export function sheetMoveTransition(phase: SheetMotionPhaseType): string {
  return `transform ${sheetMotionTiming(phase, false)}`;
}

/** The dim's transition: the panel's own timing, so its opacity is the panel's travel. */
export function sheetDimTransition(phase: SheetMotionPhaseType, reducedMotion: boolean): string {
  return `opacity ${sheetMotionTiming(phase, reducedMotion)}`;
}

/** Where a drawer comes from: the bottom (the work-area picker) or the leading edge (Navigation). */
export type DrawerEdgeType = 'bottom' | 'leading';

export interface IDrawerMotion {
  readonly phase: SheetMotionPhaseType;
  /** The panel's style: its travel, or under Reduce Motion its fade. */
  readonly panelStyle: CSSProperties;
  /** The backdrop's style: opacity on the panel's own timing. */
  readonly backdropStyle: CSSProperties;
  /** A dismissal the person made (✕, the backdrop, Escape): the drawer leaves, then `onClose`. */
  readonly requestClose: () => void;
}

/**
 * A `ModalFrame` drawer's enter and exit, on the sheet's timing. Mount the drawer only while it is
 * open, as `ModalFrame` asks; the first frame is drawn at the start (off screen, transparent) and the
 * next moves to rest. A close the person makes plays the exit first; a close the app makes (the
 * person chose something, so the screen changes anyway) unmounts at once.
 */
export function useDrawerMotion(edge: DrawerEdgeType, onClose: () => void): IDrawerMotion {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [phase, setPhase] = useState<SheetMotionPhaseType>('entering');
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The first frame at the start, then a transition to rest. Two frames, so the start is painted
  // (a style change inside one frame is no transition at all).
  useLayoutEffect(() => {
    if (phase !== 'entering') return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setPhase('open'));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [phase]);

  useEffect(
    () => () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    },
    [],
  );

  const requestClose = useCallback(() => {
    if (exitTimer.current) return;
    setPhase('exiting');
    exitTimer.current = setTimeout(
      () => {
        exitTimer.current = null;
        onCloseRef.current();
      },
      reducedMotion ? SHEET_FADE_MS : SHEET_EXIT_MS,
    );
  }, [reducedMotion]);

  const atRest = phase === 'open';
  const away = edge === 'bottom' ? 'translateY(100%)' : 'translateX(-100%)';
  const panelStyle: CSSProperties = reducedMotion
    ? { opacity: atRest ? 1 : 0, transition: `opacity ${sheetMotionTiming(phase, true)}` }
    : { transform: atRest ? 'none' : away, transition: sheetMoveTransition(phase) };
  const backdropStyle: CSSProperties = {
    opacity: atRest ? 1 : 0,
    transition: sheetDimTransition(phase, reducedMotion),
  };
  return { phase, panelStyle, backdropStyle, requestClose };
}
